import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { Box, Typography, CircularProgress, Alert, Container, Paper } from '@mui/material';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';

/**
 * Page de callback pour l'authentification AppStore
 * Récupère les paramètres depuis l'URL et authentifie l'utilisateur
 */
const AppStoreCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const authenticateFromUrl = async () => {
      console.log('[AUTH DEBUG] AppStoreCallback: Initialisation du processus d\'authentification');
      console.log('[AUTH DEBUG] AppStoreCallback: URL complète:', window.location.href);
      
      try {
        // Récupérer les paramètres depuis l'URL
        const params = new URLSearchParams(location.search);
        console.log('[AUTH DEBUG] AppStoreCallback: Paramètres URL:', location.search);
        
        const token = params.get('token');
        const authToken = params.get('auth_token'); // Vérifier les deux formats possibles
        const finalToken = token || authToken;
        
        console.log('[AUTH DEBUG] AppStoreCallback: Token trouvé (param "token"):', token ? 'Oui' : 'Non');
        console.log('[AUTH DEBUG] AppStoreCallback: Token trouvé (param "auth_token"):', authToken ? 'Oui' : 'Non');
        
        const redirectUrl = params.get('redirectUrl') || '/';
        console.log('[AUTH DEBUG] AppStoreCallback: URL de redirection:', redirectUrl);
        
        if (!finalToken) {
          console.error('[AUTH DEBUG] AppStoreCallback: Aucun token trouvé dans l\'URL');
          throw new Error('Token manquant dans l\'URL');
        }
        
        console.log('[AUTH DEBUG] AppStoreCallback: Token valide trouvé, stockage dans localStorage');
        
        // Stocker le token dans localStorage pour le système d'authentification JWT personnalisé
        localStorage.setItem('auth_token', finalToken);
        // Ajouter un timestamp pour contourner les problèmes de cache dans Chrome
        localStorage.setItem('auth_timestamp', Date.now().toString());
        console.log('[AUTH DEBUG] AppStoreCallback: Token JWT stocké dans localStorage avec timestamp');
        
        // Authentifier avec Firebase
        console.log('[AUTH DEBUG] AppStoreCallback: Tentative d\'authentification Firebase avec le token');
        const userCredential = await signInWithCustomToken(auth, finalToken);
        const user = userCredential.user;
        console.log('[AUTH DEBUG] AppStoreCallback: Authentification Firebase réussie');
        console.log('[AUTH DEBUG] AppStoreCallback: Utilisateur connecté:', user.uid, user.email);
        
        // Mettre à jour la date de dernière connexion dans Firestore
        if (user) {
          try {
            console.log('[AUTH DEBUG] AppStoreCallback: Mise à jour de la date de dernière connexion dans Firestore');
            await setDoc(doc(db, 'users', user.uid), {
              lastLogin: serverTimestamp(),
            }, { merge: true });
            console.log('[AUTH DEBUG] AppStoreCallback: Mise à jour Firestore réussie');
          } catch (firestoreError) {
            console.error('[AUTH DEBUG] AppStoreCallback: Erreur lors de la mise à jour de lastLogin:', firestoreError);
            // Ne pas bloquer l'authentification si cette étape échoue
          }
        }
        
        // Rediriger vers l'URL spécifiée ou la page d'accueil
        console.log('[AUTH DEBUG] AppStoreCallback: Redirection vers', redirectUrl);
        navigate(redirectUrl, { replace: true });
      } catch (error: any) {
        console.error('[AUTH DEBUG] AppStoreCallback: Erreur d\'authentification:', error);
        console.error('[AUTH DEBUG] AppStoreCallback: Message d\'erreur:', error.message);
        setError(`Échec de l'authentification: ${error.message}`);
        setLoading(false);
      }
    };
    
    console.log('[AUTH DEBUG] AppStoreCallback: Composant monté, démarrage de l\'authentification');
    authenticateFromUrl();
  }, [location.search, navigate]);
  
  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Authentification AppStore
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
            <CircularProgress size={60} thickness={4} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Connexion en cours...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </Paper>
    </Container>
  );
};

export default AppStoreCallback;
